import React, { useState, useEffect, useMemo, useCallback } from "react"
import { useNavigate } from "react-router-dom"
import {
  Box,
  Typography,
  Paper,
  Button,
  Stack,
  TextField,
  Chip,
  CircularProgress,
  Divider,
  InputAdornment,
  Fade,
  Alert,
  IconButton,
  Tooltip,
} from "@mui/material"
import AddIcon from "@mui/icons-material/Add"
import FolderOpenIcon from "@mui/icons-material/FolderOpen"
import SearchIcon from "@mui/icons-material/Search"
import ArrowForwardIcon from "@mui/icons-material/ArrowForward"
import DescriptionIcon from "@mui/icons-material/Description"
import PersonIcon from "@mui/icons-material/Person"
import CalendarTodayIcon from "@mui/icons-material/CalendarToday"
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart"
import StorefrontIcon from "@mui/icons-material/Storefront"
import BusinessIcon from "@mui/icons-material/Business"
import PersonAddIcon from "@mui/icons-material/PersonAdd"
import CheckCircleIcon from "@mui/icons-material/CheckCircle"
import PhoneIcon from "@mui/icons-material/Phone"
import EmailIcon from "@mui/icons-material/Email"
import LocationOnIcon from "@mui/icons-material/LocationOn"
import BadgeIcon from "@mui/icons-material/Badge"
import LogoutIcon from "@mui/icons-material/Logout"
import { useTranslation } from "react-i18next"
import { isB2BUser, isRetailUser, isAdminUser } from "../../../constants/permissions"
import { getCustomers, postCustomer, searchCustomers } from "../../../helpers/api_helper"
import { validateVAT } from "../../../helpers/aedevat"

// ─── Eliton Brand Colors ──────────────────────────────────────────
const ELITON = {
  red: "#e50019",
  redDark: "#c40016",
  dark: "#212121",
  white: "#ffffff",
  gray: "#f4f4f4",
  grayMed: "#858585",
}

// ─── Eliton Logo SVG ────────────────────────────────────────────
const ElitonLogo = ({ height = 28 }) => (
  <svg xmlns="http://www.w3.org/2000/svg" width={height * (114.882 / 34.923)} height={height} viewBox="0 0 114.882 34.923">
    <defs><clipPath id="eliton-gate-clip"><rect width="114.882" height="34.923" fill="none" /></clipPath></defs>
    <g transform="translate(0 0)">
      <rect width="3.844" height="26.376" transform="translate(41.399 7.835)" fill="#fff" />
      <g clipPath="url(#eliton-gate-clip)">
        <path d="M66.085,3.8l-.1-.584L65.7,2.681l-.438-.438L64.772,2l-.633-.1-.584.1-.536.243-.389.438-.292.535-.1.584.1.584A1.91,1.91,0,0,0,65.7,4.92l.292-.535Z" transform="translate(-20.843 -0.637)" fill="#fff" />
        <rect width="3.796" height="34.211" transform="translate(31.909)" fill="#fff" />
        <path d="M22.857,22.815q-.219-.656-.438-1.314a9.706,9.706,0,0,0-17.812,0l-.486,1.314Zm-18.979,3.8c1.478,8.576,11.592,11.457,17.13,4.964l.779-1.168h4.283a24.9,24.9,0,0,1-1.606,2.871A13.516,13.516,0,0,1,.082,23.788q.146-.8.292-1.606C4.394,7.887,24.3,8.659,26.9,23.4a18.568,18.568,0,0,1,.049,3.212H3.878" transform="translate(0 -3.982)" fill="#fff" />
        <path d="M80.378,26.133l.1.925A4.874,4.874,0,0,0,85.1,31.049l.925-.1V34.8l-1.168.049A8.77,8.77,0,0,1,76.631,27.2l-.049-1.07V0h3.8V8.273h5.645V12.02H80.378Z" transform="translate(-25.646 0)" fill="#fff" />
        <path d="M115.157,20.182l-.827-1.119a9.737,9.737,0,0,0-17.082,7.251l.243,1.363-3.552,1.508L93.6,27.579a13.52,13.52,0,0,1,24.187-10.268l.876,1.363Z" transform="translate(-31.289 -3.963)" fill={ELITON.red} />
        <path d="M117.6,28.946l.146,1.363a9.739,9.739,0,0,1-16.692,7.056l-.876-1.022L96.63,37.852l.973,1.314a13.509,13.509,0,0,0,23.846-10.122l-.292-1.606Z" transform="translate(-32.359 -9.189)" fill="#fff" />
        <path d="M143.668,23.266l.1-1.119a7.119,7.119,0,0,1,14.064,0l.1,1.119V38.353h3.8V23.266L161.673,22c-2.1-13.495-21.115-12.534-21.8,1.12V38.353h3.8Z" transform="translate(-46.84 -4.141)" fill="#fff" />
      </g>
    </g>
  </svg>
)

const STATUS_COLORS = {
  draft: { bg: "#f1f5f9", color: "#475569", label: "Draft" },
  pending_approval: { bg: "#fef3c7", color: "#92400e", label: "Pending Approval" },
  approved: { bg: "#dcfce7", color: "#166534", label: "Approved" },
  changes_requested: { bg: "#fee2e2", color: "#991b1b", label: "Changes Requested" },
  revised: { bg: "#dbeafe", color: "#1e40af", label: "Revised by Specialist" },
  final_accepted: { bg: "#d1fae5", color: "#065f46", label: "Final Accepted" },
}

const EMPTY_CUST_FORM = {
  vat: "", name: "", company: "", phone: "", email: "",
  address: "", city: "", tax_office: "",
}

const QuoteGatePage = ({ onQuoteReady, onNewQuote, quotes, loading, quoteHistory }) => {
  const { t } = useTranslation()
  const navigate = useNavigate()

  const handleLogout = () => {
    localStorage.removeItem("authUser")
    navigate("/login")
  }

  // ── User info ──
  const currentUser = useMemo(() => {
    try { return JSON.parse(localStorage.getItem("authUser")) || {} } catch { return {} }
  }, [])
  const permissions = currentUser.permissions || {}
  const isB2B = isB2BUser(permissions)
  const isAdmin = isAdminUser(permissions)

  // ── Mode state ──
  // null            → landing (New Quote / Load Existing)
  // "type"          → choose Retail or B2B (admin only)
  // "customer"      → search & select customer (retail only)
  // "b2b_customer"  → free-text customer form (B2B only)
  // "create_cust"   → create new customer form (retail only)
  // "details"       → quote title + margin + Start Building
  // "existing"      → existing quotes list
  const [mode, setMode] = useState(isB2B ? "existing" : null)
  const [quoteType, setQuoteType] = useState(isB2B ? "b2b" : null)

  // ── Customer state ──
  const [customers, setCustomers] = useState([])
  const [customersLoading, setCustomersLoading] = useState(false)
  const [customerSearch, setCustomerSearch] = useState("")
  const [selectedCustomer, setSelectedCustomer] = useState(null)

  // ── Create customer form ──
  const [newCust, setNewCust] = useState(EMPTY_CUST_FORM)
  const [vatValidating, setVatValidating] = useState(false)
  const [vatValidated, setVatValidated] = useState(false)
  const [vatError, setVatError] = useState("")
  const [savingCustomer, setSavingCustomer] = useState(false)
  const [customerError, setCustomerError] = useState("")

  // ── Quote details state ──
  const [quoteTitle, setQuoteTitle] = useState("")
  const [margin, setMargin] = useState(() => {
    try {
      const saved = localStorage.getItem("storeSettings_undefined")
      if (saved) return JSON.parse(saved).profitMargin || 0
    } catch { /* ignore */ }
    return 0
  })
  const [creating, setCreating] = useState(false)

  // ── Existing quotes search ──
  const [search, setSearch] = useState("")

  // ── Server-side customer search ──
  // The customers table is large (~16K rows on eliton). Loading all of them
  // crashes the UI, so we hit the server on every keystroke (debounced).
  const fetchCustomers = useCallback(async (query = "") => {
    setCustomersLoading(true)
    try {
      const data = await searchCustomers(query, 50)
      setCustomers(Array.isArray(data) ? data : [])
    } catch (err) {
      console.error("[QuoteGate] Failed to search customers:", err)
      setCustomers([])
    } finally {
      setCustomersLoading(false)
    }
  }, [])

  // Initial load (first 50 alphabetically) when entering customer mode
  useEffect(() => {
    if (mode === "customer") fetchCustomers("")
  }, [mode, fetchCustomers])

  // Debounced re-search when the user types
  useEffect(() => {
    if (mode !== "customer") return
    const handle = setTimeout(() => { fetchCustomers(customerSearch) }, 300)
    return () => clearTimeout(handle)
  }, [customerSearch, mode, fetchCustomers])

  // Server already filters + limits, just sort defensively in case the API drifts
  const filteredCustomers = useMemo(
    () => [...customers].sort((a, b) => (a.name || "").localeCompare(b.name || "")),
    [customers]
  )

  // ── Filtered existing quotes ──
  const filteredQuotes = useMemo(() => {
    if (!quotes || quotes.length === 0) return []
    if (!search.trim()) return quotes
    const s = search.toLowerCase()
    return quotes.filter(q =>
      (q.title || "").toLowerCase().includes(s) ||
      (q.customer_name || "").toLowerCase().includes(s)
    )
  }, [quotes, search])

  // ── Navigation handlers ──
  const handleNewQuoteClick = () => {
    if (isAdmin) {
      setMode("type")
    } else if (isB2B) {
      setQuoteType("b2b")
      setNewCust(EMPTY_CUST_FORM)
      setVatValidated(false)
      setVatError("")
      setMode("b2b_customer")
    } else {
      setQuoteType("retail")
      setMode("customer")
    }
  }

  const handleBack = () => {
    if (mode === "details") {
      setSelectedCustomer(null)
      if (quoteType === "b2b") {
        setMode("b2b_customer")
      } else {
        setMode("customer")
      }
    } else if (mode === "b2b_customer") {
      setNewCust(EMPTY_CUST_FORM)
      setVatValidated(false)
      setVatError("")
      if (isAdmin) setMode("type")
      else setMode("existing")
    } else if (mode === "create_cust") {
      setNewCust(EMPTY_CUST_FORM)
      setVatValidated(false)
      setVatError("")
      setCustomerError("")
      setMode("customer")
    } else if (mode === "customer") {
      setCustomerSearch("")
      if (isAdmin) setMode("type")
      else setMode(null)
    } else if (mode === "type") {
      setMode(null)
    } else if (mode === "existing" && !isB2B) {
      setMode(null)
    }
  }

  // ── Type selection ──
  const handleTypeSelect = (type) => {
    setQuoteType(type)
    if (type === "b2b") {
      setNewCust(EMPTY_CUST_FORM)
      setVatValidated(false)
      setVatError("")
      setMode("b2b_customer")
    } else {
      setMode("customer")
    }
  }

  // ── Customer selection ──
  const handleSelectCustomer = (customer) => {
    setSelectedCustomer(customer)
    setMode("details")
  }

  // ── B2B customer continue (free-text, no DB save) ──
  const handleB2bCustomerContinue = () => {
    if (!newCust.name.trim()) return
    setSelectedCustomer({
      name: newCust.name,
      vat: newCust.vat,
      phone: newCust.phone,
      email: newCust.email,
      address: newCust.address,
      city: newCust.city,
      tax_office: newCust.tax_office,
    })
    setMode("details")
  }

  // ── VAT validation ──
  const handleValidateVAT = async () => {
    if (!newCust.vat.trim()) return
    setVatValidating(true)
    setVatError("")
    setVatValidated(false)
    try {
      const result = await validateVAT(newCust.vat.trim())
      if (result?.success && result.data?.basicInfo) {
        const info = result.data.basicInfo
        setNewCust(prev => ({
          ...prev,
          name: info.onomasia || prev.name,
          address: info.postal_address || prev.address,
          city: info.postal_area_description || prev.city,
          tax_office: info.doy_descr || prev.tax_office,
        }))
        setVatValidated(true)
      } else {
        setVatError(t("VAT not found or invalid"))
      }
    } catch (err) {
      setVatError(err.message || t("VAT validation failed"))
    } finally {
      setVatValidating(false)
    }
  }

  // ── Save new customer ──
  const handleSaveCustomer = async () => {
    if (!newCust.name.trim()) return
    setSavingCustomer(true)
    setCustomerError("")
    try {
      const custData = {}
      if (newCust.name) custData.name = newCust.name
      if (newCust.vat) custData.vat = newCust.vat
      if (newCust.phone) custData.phone = newCust.phone
      if (newCust.email) custData.email = newCust.email
      if (newCust.address) custData.address = newCust.address
      if (newCust.city) custData.city = newCust.city
      if (newCust.tax_office) custData.tax_office = newCust.tax_office
      if (newCust.company) custData.company = newCust.company

      const result = await postCustomer({ resource: [custData] })
      if (result?.resource?.[0]) {
        const created = { ...custData, ...result.resource[0] }
        setSelectedCustomer(created)
        setMode("details")
      } else {
        setCustomerError(t("Failed to create customer"))
      }
    } catch (err) {
      console.error("[QuoteGate] Create customer failed:", err)
      setCustomerError(err.message || t("Failed to create customer"))
    } finally {
      setSavingCustomer(false)
    }
  }

  // ── Create quote ──
  const handleCreateQuote = async () => {
    if (!selectedCustomer) return
    setCreating(true)
    try {
      const name = selectedCustomer.name || ""
      const result = await quoteHistory.saveQuote(
        quoteTitle || `${name} - ${new Date().toLocaleDateString("el-GR")}`,
        name,
        selectedCustomer.phone || "",
        selectedCustomer.email || "",
        selectedCustomer.address || "",
        selectedCustomer.id || null,
      )
      if (result) {
        onNewQuote(result, quoteType === "b2b" ? margin : 0)
      }
    } catch (err) {
      console.error("[QuoteGate] Create quote failed:", err)
    } finally {
      setCreating(false)
    }
  }

  const handleLoadQuote = (quote) => {
    onQuoteReady(quote)
  }

  // ── Reusable header ──
  const Header = ({ title, showBack = true, rightContent }) => (
    <Box sx={{ py: 2, px: 3, borderBottom: `3px solid ${ELITON.red}` }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between">
        <Stack direction="row" alignItems="center" spacing={2}>
          <ElitonLogo height={24} />
          <Divider orientation="vertical" flexItem sx={{ borderColor: "rgba(255,255,255,0.15)" }} />
          <Typography sx={{ fontSize: "0.72rem", color: "rgba(255,255,255,0.5)", fontWeight: 500, letterSpacing: 0.5 }}>
            {title}
          </Typography>
        </Stack>
        <Stack direction="row" spacing={1} alignItems="center">
          {rightContent}
          {showBack && (
            <Button
              size="small"
              onClick={handleBack}
              sx={{ color: "rgba(255,255,255,0.5)", textTransform: "none", "&:hover": { color: "#fff" } }}
            >
              {t("Back")}
            </Button>
          )}
          <Tooltip title={t("Logout")} arrow>
            <IconButton size="small" onClick={handleLogout} sx={{ color: "rgba(255,255,255,0.5)", "&:hover": { color: ELITON.red } }}>
              <LogoutIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Tooltip>
        </Stack>
      </Stack>
    </Box>
  )

  // ═══════════════════════════════════════════════════════════════
  // MODE: null — Landing (New Quote / Load Existing)
  // ═══════════════════════════════════════════════════════════════
  if (mode === null) {
    return (
      <Box sx={{ minHeight: "100vh", bgcolor: ELITON.dark }}>
        <Box sx={{ py: 2, px: 3, borderBottom: `3px solid ${ELITON.red}` }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <Stack direction="row" alignItems="center" spacing={2}>
              <ElitonLogo height={28} />
              <Divider orientation="vertical" flexItem sx={{ borderColor: "rgba(255,255,255,0.15)" }} />
              <Typography sx={{ fontSize: "0.72rem", color: "rgba(255,255,255,0.5)", fontWeight: 500, letterSpacing: 0.5 }}>
                Quote Composer
              </Typography>
            </Stack>
            <Tooltip title={t("Logout")} arrow>
              <IconButton size="small" onClick={handleLogout} sx={{ color: "rgba(255,255,255,0.5)", "&:hover": { color: ELITON.red } }}>
                <LogoutIcon sx={{ fontSize: 20 }} />
              </IconButton>
            </Tooltip>
          </Stack>
        </Box>

        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", minHeight: "calc(100vh - 60px)" }}>
          <Fade in timeout={500}>
            <Stack spacing={4} alignItems="center" sx={{ maxWidth: 600, px: 3 }}>
              <Typography variant="h4" sx={{ fontWeight: 800, color: "#fff", textAlign: "center" }}>
                {t("Quote Composer")}
              </Typography>
              <Typography sx={{ fontSize: "0.9rem", color: "rgba(255,255,255,0.5)", textAlign: "center" }}>
                {t("Create a new quote or continue working on an existing one")}
              </Typography>

              <Stack direction={{ xs: "column", sm: "row" }} spacing={3} sx={{ width: "100%", maxWidth: 500 }}>
                <Paper
                  elevation={0}
                  onClick={handleNewQuoteClick}
                  sx={{
                    flex: 1, p: 4, cursor: "pointer", borderRadius: 3,
                    border: "2px solid transparent", bgcolor: "#fff", textAlign: "center",
                    transition: "all 0.2s",
                    "&:hover": { borderColor: ELITON.red, transform: "translateY(-4px)", boxShadow: "0 12px 32px rgba(229,0,25,0.15)" },
                  }}
                >
                  <AddIcon sx={{ fontSize: 48, color: ELITON.red, mb: 1.5 }} />
                  <Typography sx={{ fontWeight: 700, fontSize: "1.1rem", color: "#1e293b", mb: 0.5 }}>
                    {t("New Quote")}
                  </Typography>
                  <Typography sx={{ fontSize: "0.78rem", color: "#64748b" }}>
                    {t("Start a fresh kitchen composition")}
                  </Typography>
                </Paper>

                <Paper
                  elevation={0}
                  onClick={() => setMode("existing")}
                  sx={{
                    flex: 1, p: 4, cursor: "pointer", borderRadius: 3,
                    border: "2px solid transparent", bgcolor: "#fff", textAlign: "center",
                    transition: "all 0.2s",
                    "&:hover": { borderColor: "#3b82f6", transform: "translateY(-4px)", boxShadow: "0 12px 32px rgba(59,130,246,0.15)" },
                  }}
                >
                  <FolderOpenIcon sx={{ fontSize: 48, color: "#3b82f6", mb: 1.5 }} />
                  <Typography sx={{ fontWeight: 700, fontSize: "1.1rem", color: "#1e293b", mb: 0.5 }}>
                    {t("Load Existing")}
                  </Typography>
                  <Typography component="span" sx={{ fontSize: "0.78rem", color: "#64748b", display: "block" }}>
                    {t("Continue a saved quote")}
                    {quotes?.length > 0 && (
                      <Chip
                        label={quotes.length}
                        size="small"
                        sx={{ ml: 0.75, height: 18, fontSize: "0.6rem", fontWeight: 700, bgcolor: "#dbeafe", color: "#1d4ed8" }}
                      />
                    )}
                  </Typography>
                </Paper>
              </Stack>
            </Stack>
          </Fade>
        </Box>
      </Box>
    )
  }

  // ═══════════════════════════════════════════════════════════════
  // MODE: "type" — Choose Retail or B2B (admin only)
  // ═══════════════════════════════════════════════════════════════
  if (mode === "type") {
    return (
      <Box sx={{ minHeight: "100vh", bgcolor: ELITON.dark }}>
        <Header title={t("New Quote")} />

        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", minHeight: "calc(100vh - 60px)" }}>
          <Fade in timeout={400}>
            <Stack spacing={4} alignItems="center" sx={{ maxWidth: 600, px: 3 }}>
              <Typography variant="h5" sx={{ fontWeight: 800, color: "#fff", textAlign: "center" }}>
                {t("Select Quote Type")}
              </Typography>
              <Typography sx={{ fontSize: "0.85rem", color: "rgba(255,255,255,0.5)", textAlign: "center" }}>
                {t("Choose the type of quote you want to create")}
              </Typography>

              <Stack direction={{ xs: "column", sm: "row" }} spacing={3} sx={{ width: "100%", maxWidth: 500 }}>
                <Paper
                  elevation={0}
                  onClick={() => handleTypeSelect("retail")}
                  sx={{
                    flex: 1, p: 4, cursor: "pointer", borderRadius: 3,
                    border: "2px solid transparent", bgcolor: "#fff", textAlign: "center",
                    transition: "all 0.2s",
                    "&:hover": { borderColor: ELITON.red, transform: "translateY(-4px)", boxShadow: "0 12px 32px rgba(229,0,25,0.15)" },
                  }}
                >
                  <StorefrontIcon sx={{ fontSize: 48, color: ELITON.red, mb: 1.5 }} />
                  <Typography sx={{ fontWeight: 700, fontSize: "1.1rem", color: "#1e293b", mb: 0.5 }}>
                    {t("Retail")}
                  </Typography>
                  <Typography sx={{ fontSize: "0.78rem", color: "#64748b" }}>
                    {t("Search or create a new customer")}
                  </Typography>
                </Paper>

                <Paper
                  elevation={0}
                  onClick={() => handleTypeSelect("b2b")}
                  sx={{
                    flex: 1, p: 4, cursor: "pointer", borderRadius: 3,
                    border: "2px solid transparent", bgcolor: "#fff", textAlign: "center",
                    transition: "all 0.2s",
                    "&:hover": { borderColor: "#3b82f6", transform: "translateY(-4px)", boxShadow: "0 12px 32px rgba(59,130,246,0.15)" },
                  }}
                >
                  <BusinessIcon sx={{ fontSize: 48, color: "#3b82f6", mb: 1.5 }} />
                  <Typography sx={{ fontWeight: 700, fontSize: "1.1rem", color: "#1e293b", mb: 0.5 }}>
                    {t("B2B")}
                  </Typography>
                  <Typography sx={{ fontSize: "0.78rem", color: "#64748b" }}>
                    {t("Enter customer details directly")}
                  </Typography>
                </Paper>
              </Stack>
            </Stack>
          </Fade>
        </Box>
      </Box>
    )
  }

  // ═══════════════════════════════════════════════════════════════
  // MODE: "customer" — Search & Select Customer
  // ═══════════════════════════════════════════════════════════════
  if (mode === "customer") {
    return (
      <Box sx={{ minHeight: "100vh", bgcolor: ELITON.dark }}>
        <Header title={t("Select Customer")} />

        <Box sx={{ maxWidth: 600, mx: "auto", pt: 4, px: 3 }}>
          <Fade in timeout={400}>
            <Box>
              <Typography variant="h5" sx={{ fontWeight: 800, color: "#fff", mb: 0.5 }}>
                {t("Find Customer")}
              </Typography>
              <Typography sx={{ fontSize: "0.82rem", color: "rgba(255,255,255,0.5)", mb: 3 }}>
                {t("Search by name, VAT, phone, or email")}
              </Typography>

              <TextField
                placeholder={t("Search customers...")}
                value={customerSearch}
                onChange={(e) => setCustomerSearch(e.target.value)}
                fullWidth
                size="small"
                autoFocus
                InputProps={{
                  startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 18, color: "#94a3b8" }} /></InputAdornment>,
                }}
                sx={{
                  mb: 2,
                  "& .MuiOutlinedInput-root": { bgcolor: "#fff", borderRadius: 2 },
                }}
              />

              {customersLoading ? (
                <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
                  <CircularProgress sx={{ color: ELITON.red }} />
                </Box>
              ) : filteredCustomers.length === 0 ? (
                <Paper sx={{ p: 4, textAlign: "center", borderRadius: 3 }}>
                  <PersonIcon sx={{ fontSize: 48, color: "#d1d5db", mb: 1 }} />
                  <Typography sx={{ color: "#64748b" }}>
                    {customerSearch.trim()
                      ? t("No customers match your search")
                      : t("No customers found")}
                  </Typography>
                </Paper>
              ) : (
                <Stack spacing={1} sx={{ maxHeight: "calc(100vh - 340px)", overflowY: "auto", pr: 0.5 }}>
                  {filteredCustomers.map((customer) => (
                    <Paper
                      key={customer.id}
                      elevation={0}
                      onClick={() => handleSelectCustomer(customer)}
                      sx={{
                        p: 2, borderRadius: 2, cursor: "pointer",
                        border: "1px solid #e2e8f0", transition: "all 0.15s",
                        "&:hover": { borderColor: ELITON.red, boxShadow: "0 4px 12px rgba(0,0,0,0.06)", transform: "translateX(4px)" },
                      }}
                    >
                      <Stack direction="row" justifyContent="space-between" alignItems="center">
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography sx={{ fontWeight: 700, fontSize: "0.92rem", color: "#1e293b", mb: 0.25 }}>
                            {customer.name || t("Unnamed")}
                          </Typography>
                          <Stack direction="row" spacing={2} flexWrap="wrap" sx={{ gap: 0.5 }}>
                            {customer.vat && (
                              <Stack direction="row" alignItems="center" spacing={0.5}>
                                <BadgeIcon sx={{ fontSize: 13, color: "#94a3b8" }} />
                                <Typography sx={{ fontSize: "0.72rem", color: "#64748b" }}>
                                  {customer.vat}
                                </Typography>
                              </Stack>
                            )}
                            {customer.phone && (
                              <Stack direction="row" alignItems="center" spacing={0.5}>
                                <PhoneIcon sx={{ fontSize: 13, color: "#94a3b8" }} />
                                <Typography sx={{ fontSize: "0.72rem", color: "#64748b" }}>
                                  {customer.phone}
                                </Typography>
                              </Stack>
                            )}
                            {customer.address && (
                              <Stack direction="row" alignItems="center" spacing={0.5}>
                                <LocationOnIcon sx={{ fontSize: 13, color: "#94a3b8" }} />
                                <Typography sx={{ fontSize: "0.72rem", color: "#64748b", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: 200 }}>
                                  {customer.address}
                                </Typography>
                              </Stack>
                            )}
                          </Stack>
                        </Box>
                        <ArrowForwardIcon sx={{ fontSize: 18, color: "#94a3b8", ml: 1, flexShrink: 0 }} />
                      </Stack>
                    </Paper>
                  ))}
                </Stack>
              )}

              {/* Create New Customer button — retail only */}
              {quoteType === "retail" && (
                <Button
                  fullWidth
                  variant="outlined"
                  startIcon={<PersonAddIcon />}
                  onClick={() => {
                    setNewCust(EMPTY_CUST_FORM)
                    setVatValidated(false)
                    setVatError("")
                    setCustomerError("")
                    setMode("create_cust")
                  }}
                  sx={{
                    mt: 2, py: 1.25, textTransform: "none", fontWeight: 600,
                    borderColor: "rgba(255,255,255,0.2)", color: "#fff", borderRadius: 2,
                    "&:hover": { borderColor: ELITON.red, bgcolor: "rgba(229,0,25,0.08)" },
                  }}
                >
                  {t("Create New Customer")}
                </Button>
              )}
            </Box>
          </Fade>
        </Box>
      </Box>
    )
  }

  // ═══════════════════════════════════════════════════════════════
  // MODE: "b2b_customer" — Free-text Customer Form (B2B only)
  // ═══════════════════════════════════════════════════════════════
  if (mode === "b2b_customer") {
    return (
      <Box sx={{ minHeight: "100vh", bgcolor: ELITON.dark }}>
        <Header title={t("Customer Details")} />

        <Box sx={{ display: "flex", justifyContent: "center", pt: 4, px: 3 }}>
          <Fade in timeout={400}>
            <Paper sx={{ maxWidth: 520, width: "100%", p: 4, borderRadius: 3 }}>
              <Typography variant="h5" sx={{ fontWeight: 800, color: "#1e293b", mb: 0.5 }}>
                {t("Customer Details")}
              </Typography>
              <Typography sx={{ fontSize: "0.82rem", color: "#64748b", mb: 3 }}>
                {t("Enter customer information for this quote")}
              </Typography>

              <Stack spacing={2.5}>
                {/* VAT + Validate */}
                <Stack direction="row" spacing={1.5} alignItems="flex-start">
                  <TextField
                    label={t("VAT Number")}
                    value={newCust.vat}
                    onChange={(e) => {
                      setNewCust(prev => ({ ...prev, vat: e.target.value }))
                      setVatValidated(false)
                      setVatError("")
                    }}
                    fullWidth
                    size="small"
                    InputProps={{
                      startAdornment: <InputAdornment position="start"><BadgeIcon sx={{ fontSize: 18, color: "#94a3b8" }} /></InputAdornment>,
                      endAdornment: vatValidated ? <CheckCircleIcon sx={{ fontSize: 18, color: "#16a34a" }} /> : null,
                    }}
                  />
                  <Button
                    variant="contained"
                    onClick={handleValidateVAT}
                    disabled={!newCust.vat.trim() || vatValidating}
                    sx={{
                      minWidth: 100, textTransform: "none", fontWeight: 600,
                      bgcolor: "#3b82f6", whiteSpace: "nowrap", height: 40,
                      "&:hover": { bgcolor: "#2563eb" },
                    }}
                  >
                    {vatValidating ? <CircularProgress size={18} color="inherit" /> : t("Validate")}
                  </Button>
                </Stack>

                {vatError && <Alert severity="error" sx={{ py: 0 }}>{vatError}</Alert>}
                {vatValidated && <Alert severity="success" sx={{ py: 0 }}>{t("VAT validated successfully")}</Alert>}

                <Divider />

                {/* Customer fields */}
                <TextField
                  label={t("Name") + " *"}
                  value={newCust.name}
                  onChange={(e) => setNewCust(prev => ({ ...prev, name: e.target.value }))}
                  fullWidth
                  size="small"
                  autoFocus
                  InputProps={{
                    startAdornment: <InputAdornment position="start"><PersonIcon sx={{ fontSize: 18, color: "#94a3b8" }} /></InputAdornment>,
                  }}
                />
                <Stack direction="row" spacing={2}>
                  <TextField
                    label={t("Phone")}
                    value={newCust.phone}
                    onChange={(e) => setNewCust(prev => ({ ...prev, phone: e.target.value }))}
                    fullWidth
                    size="small"
                    InputProps={{
                      startAdornment: <InputAdornment position="start"><PhoneIcon sx={{ fontSize: 18, color: "#94a3b8" }} /></InputAdornment>,
                    }}
                  />
                  <TextField
                    label={t("Email")}
                    value={newCust.email}
                    onChange={(e) => setNewCust(prev => ({ ...prev, email: e.target.value }))}
                    fullWidth
                    size="small"
                    InputProps={{
                      startAdornment: <InputAdornment position="start"><EmailIcon sx={{ fontSize: 18, color: "#94a3b8" }} /></InputAdornment>,
                    }}
                  />
                </Stack>
                <TextField
                  label={t("Address")}
                  value={newCust.address}
                  onChange={(e) => setNewCust(prev => ({ ...prev, address: e.target.value }))}
                  fullWidth
                  size="small"
                  InputProps={{
                    startAdornment: <InputAdornment position="start"><LocationOnIcon sx={{ fontSize: 18, color: "#94a3b8" }} /></InputAdornment>,
                  }}
                />
                <TextField
                  label={t("City")}
                  value={newCust.city}
                  onChange={(e) => setNewCust(prev => ({ ...prev, city: e.target.value }))}
                  fullWidth
                  size="small"
                />

                <Button
                  variant="contained"
                  size="large"
                  endIcon={<ArrowForwardIcon />}
                  onClick={handleB2bCustomerContinue}
                  disabled={!newCust.name.trim()}
                  sx={{
                    mt: 1, textTransform: "none", fontWeight: 700, fontSize: "0.95rem",
                    bgcolor: ELITON.red, borderRadius: 2, py: 1.25,
                    "&:hover": { bgcolor: ELITON.redDark },
                  }}
                >
                  {t("Continue")}
                </Button>
              </Stack>
            </Paper>
          </Fade>
        </Box>
      </Box>
    )
  }

  // ═══════════════════════════════════════════════════════════════
  // MODE: "create_cust" — Create New Customer Form (retail only)
  // ═══════════════════════════════════════════════════════════════
  if (mode === "create_cust") {
    return (
      <Box sx={{ minHeight: "100vh", bgcolor: ELITON.dark }}>
        <Header title={t("New Customer")} />

        <Box sx={{ display: "flex", justifyContent: "center", pt: 4, px: 3 }}>
          <Fade in timeout={400}>
            <Paper sx={{ maxWidth: 520, width: "100%", p: 4, borderRadius: 3 }}>
              <Typography variant="h5" sx={{ fontWeight: 800, color: "#1e293b", mb: 0.5 }}>
                {t("Create New Customer")}
              </Typography>
              <Typography sx={{ fontSize: "0.82rem", color: "#64748b", mb: 3 }}>
                {t("Enter VAT to auto-fill details from AADE")}
              </Typography>

              <Stack spacing={2.5}>
                {/* VAT + Validate */}
                <Stack direction="row" spacing={1.5} alignItems="flex-start">
                  <TextField
                    label={t("VAT Number")}
                    value={newCust.vat}
                    onChange={(e) => {
                      setNewCust(prev => ({ ...prev, vat: e.target.value }))
                      setVatValidated(false)
                      setVatError("")
                    }}
                    fullWidth
                    size="small"
                    autoFocus
                    InputProps={{
                      startAdornment: <InputAdornment position="start"><BadgeIcon sx={{ fontSize: 18, color: "#94a3b8" }} /></InputAdornment>,
                      endAdornment: vatValidated ? <CheckCircleIcon sx={{ fontSize: 18, color: "#16a34a" }} /> : null,
                    }}
                  />
                  <Button
                    variant="contained"
                    onClick={handleValidateVAT}
                    disabled={!newCust.vat.trim() || vatValidating}
                    sx={{
                      minWidth: 100, textTransform: "none", fontWeight: 600,
                      bgcolor: "#3b82f6", whiteSpace: "nowrap", height: 40,
                      "&:hover": { bgcolor: "#2563eb" },
                    }}
                  >
                    {vatValidating ? <CircularProgress size={18} color="inherit" /> : t("Validate")}
                  </Button>
                </Stack>

                {vatError && <Alert severity="error" sx={{ py: 0 }}>{vatError}</Alert>}
                {vatValidated && <Alert severity="success" sx={{ py: 0 }}>{t("VAT validated successfully")}</Alert>}

                <Divider />

                {/* Customer fields */}
                <TextField
                  label={t("Name") + " *"}
                  value={newCust.name}
                  onChange={(e) => setNewCust(prev => ({ ...prev, name: e.target.value }))}
                  fullWidth
                  size="small"
                  InputProps={{
                    startAdornment: <InputAdornment position="start"><PersonIcon sx={{ fontSize: 18, color: "#94a3b8" }} /></InputAdornment>,
                  }}
                />
                <TextField
                  label={t("Company")}
                  value={newCust.company}
                  onChange={(e) => setNewCust(prev => ({ ...prev, company: e.target.value }))}
                  fullWidth
                  size="small"
                />
                <Stack direction="row" spacing={2}>
                  <TextField
                    label={t("Phone")}
                    value={newCust.phone}
                    onChange={(e) => setNewCust(prev => ({ ...prev, phone: e.target.value }))}
                    fullWidth
                    size="small"
                    InputProps={{
                      startAdornment: <InputAdornment position="start"><PhoneIcon sx={{ fontSize: 18, color: "#94a3b8" }} /></InputAdornment>,
                    }}
                  />
                  <TextField
                    label={t("Email")}
                    value={newCust.email}
                    onChange={(e) => setNewCust(prev => ({ ...prev, email: e.target.value }))}
                    fullWidth
                    size="small"
                    InputProps={{
                      startAdornment: <InputAdornment position="start"><EmailIcon sx={{ fontSize: 18, color: "#94a3b8" }} /></InputAdornment>,
                    }}
                  />
                </Stack>
                <TextField
                  label={t("Address")}
                  value={newCust.address}
                  onChange={(e) => setNewCust(prev => ({ ...prev, address: e.target.value }))}
                  fullWidth
                  size="small"
                  InputProps={{
                    startAdornment: <InputAdornment position="start"><LocationOnIcon sx={{ fontSize: 18, color: "#94a3b8" }} /></InputAdornment>,
                  }}
                />
                <Stack direction="row" spacing={2}>
                  <TextField
                    label={t("City")}
                    value={newCust.city}
                    onChange={(e) => setNewCust(prev => ({ ...prev, city: e.target.value }))}
                    fullWidth
                    size="small"
                  />
                  <TextField
                    label={t("Tax Office")}
                    value={newCust.tax_office}
                    onChange={(e) => setNewCust(prev => ({ ...prev, tax_office: e.target.value }))}
                    fullWidth
                    size="small"
                  />
                </Stack>

                {customerError && <Alert severity="error">{customerError}</Alert>}

                <Button
                  variant="contained"
                  size="large"
                  endIcon={savingCustomer ? <CircularProgress size={18} color="inherit" /> : <ArrowForwardIcon />}
                  onClick={handleSaveCustomer}
                  disabled={!newCust.name.trim() || savingCustomer}
                  sx={{
                    mt: 1, textTransform: "none", fontWeight: 700, fontSize: "0.95rem",
                    bgcolor: ELITON.red, borderRadius: 2, py: 1.25,
                    "&:hover": { bgcolor: ELITON.redDark },
                  }}
                >
                  {t("Save Customer")}
                </Button>
              </Stack>
            </Paper>
          </Fade>
        </Box>
      </Box>
    )
  }

  // ═══════════════════════════════════════════════════════════════
  // MODE: "details" — Quote Details (title, margin, start building)
  // ═══════════════════════════════════════════════════════════════
  if (mode === "details") {
    return (
      <Box sx={{ minHeight: "100vh", bgcolor: ELITON.dark }}>
        <Header title={t("Quote Details")} />

        <Box sx={{ display: "flex", justifyContent: "center", pt: 4, px: 3 }}>
          <Fade in timeout={400}>
            <Paper sx={{ maxWidth: 520, width: "100%", p: 4, borderRadius: 3 }}>
              <Typography variant="h5" sx={{ fontWeight: 800, color: "#1e293b", mb: 3 }}>
                {t("Quote Details")}
              </Typography>

              {/* Selected Customer Card */}
              {selectedCustomer && (
                <Paper
                  elevation={0}
                  sx={{
                    p: 2, mb: 3, borderRadius: 2,
                    bgcolor: "#f0fdf4", border: "1px solid #bbf7d0",
                  }}
                >
                  <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                    <CheckCircleIcon sx={{ fontSize: 18, color: "#16a34a" }} />
                    <Typography sx={{ fontSize: "0.72rem", color: "#16a34a", fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.5 }}>
                      {t("Selected Customer")}
                    </Typography>
                  </Stack>
                  <Typography sx={{ fontWeight: 700, fontSize: "1rem", color: "#1e293b", mb: 0.5 }}>
                    {selectedCustomer.name}
                  </Typography>
                  <Stack direction="row" spacing={2} flexWrap="wrap" sx={{ gap: 0.5 }}>
                    {selectedCustomer.vat && (
                      <Stack direction="row" alignItems="center" spacing={0.5}>
                        <BadgeIcon sx={{ fontSize: 13, color: "#64748b" }} />
                        <Typography sx={{ fontSize: "0.75rem", color: "#64748b" }}>
                          {selectedCustomer.vat}
                        </Typography>
                      </Stack>
                    )}
                    {selectedCustomer.phone && (
                      <Stack direction="row" alignItems="center" spacing={0.5}>
                        <PhoneIcon sx={{ fontSize: 13, color: "#64748b" }} />
                        <Typography sx={{ fontSize: "0.75rem", color: "#64748b" }}>
                          {selectedCustomer.phone}
                        </Typography>
                      </Stack>
                    )}
                    {selectedCustomer.email && (
                      <Stack direction="row" alignItems="center" spacing={0.5}>
                        <EmailIcon sx={{ fontSize: 13, color: "#64748b" }} />
                        <Typography sx={{ fontSize: "0.75rem", color: "#64748b" }}>
                          {selectedCustomer.email}
                        </Typography>
                      </Stack>
                    )}
                    {selectedCustomer.address && (
                      <Stack direction="row" alignItems="center" spacing={0.5}>
                        <LocationOnIcon sx={{ fontSize: 13, color: "#64748b" }} />
                        <Typography sx={{ fontSize: "0.75rem", color: "#64748b" }}>
                          {selectedCustomer.address}
                        </Typography>
                      </Stack>
                    )}
                  </Stack>
                </Paper>
              )}

              <Stack spacing={2.5}>
                <TextField
                  label={t("Quote Title")}
                  value={quoteTitle}
                  onChange={(e) => setQuoteTitle(e.target.value)}
                  fullWidth
                  size="small"
                  autoFocus
                  placeholder={t("e.g. Kitchen Renovation 2026")}
                />

                {quoteType === "b2b" && (
                  <TextField
                    label={t("Margin %")}
                    value={margin}
                    onChange={(e) => setMargin(parseFloat(e.target.value) || 0)}
                    type="number"
                    size="small"
                    sx={{ maxWidth: 150 }}
                    InputProps={{
                      endAdornment: <InputAdornment position="end">%</InputAdornment>,
                    }}
                  />
                )}

                <Button
                  variant="contained"
                  size="large"
                  endIcon={creating ? <CircularProgress size={18} color="inherit" /> : <ArrowForwardIcon />}
                  onClick={handleCreateQuote}
                  disabled={creating}
                  sx={{
                    mt: 1, textTransform: "none", fontWeight: 700, fontSize: "0.95rem",
                    bgcolor: ELITON.red, borderRadius: 2, py: 1.25,
                    "&:hover": { bgcolor: ELITON.redDark },
                  }}
                >
                  {t("Start Building")}
                </Button>
              </Stack>
            </Paper>
          </Fade>
        </Box>
      </Box>
    )
  }

  // ═══════════════════════════════════════════════════════════════
  // MODE: "existing" — Existing Quotes List
  // ═══════════════════════════════════════════════════════════════
  return (
    <Box sx={{ minHeight: "100vh", bgcolor: ELITON.dark }}>
      <Box sx={{ py: 2, px: 3, borderBottom: `3px solid ${ELITON.red}` }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Stack direction="row" alignItems="center" spacing={2}>
            <ElitonLogo height={24} />
            <Divider orientation="vertical" flexItem sx={{ borderColor: "rgba(255,255,255,0.15)" }} />
            <Typography sx={{ fontSize: "0.72rem", color: "rgba(255,255,255,0.5)", fontWeight: 500 }}>
              {t("My Quotes")}
            </Typography>
          </Stack>
          <Stack direction="row" spacing={1}>
            <Button
              size="small"
              startIcon={<AddIcon />}
              onClick={handleNewQuoteClick}
              sx={{ color: "#fff", textTransform: "none", bgcolor: ELITON.red, borderRadius: 1.5, "&:hover": { bgcolor: ELITON.redDark } }}
            >
              {t("New Quote")}
            </Button>
            {!isB2B && (
              <Button
                size="small"
                onClick={() => setMode(null)}
                sx={{ color: "rgba(255,255,255,0.5)", textTransform: "none", "&:hover": { color: "#fff" } }}
              >
                {t("Back")}
              </Button>
            )}
            <Tooltip title={t("Logout")} arrow>
              <IconButton size="small" onClick={handleLogout} sx={{ color: "rgba(255,255,255,0.5)", "&:hover": { color: ELITON.red } }}>
                <LogoutIcon sx={{ fontSize: 18 }} />
              </IconButton>
            </Tooltip>
          </Stack>
        </Stack>
      </Box>

      <Box sx={{ maxWidth: 700, mx: "auto", pt: 4, px: 3 }}>
        <Fade in timeout={400}>
          <Box>
            <TextField
              placeholder={t("Search quotes...")}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              fullWidth
              size="small"
              InputProps={{
                startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 18, color: "#94a3b8" }} /></InputAdornment>,
              }}
              sx={{
                mb: 3,
                "& .MuiOutlinedInput-root": { bgcolor: "#fff", borderRadius: 2 },
              }}
            />

            {loading ? (
              <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
                <CircularProgress sx={{ color: ELITON.red }} />
              </Box>
            ) : filteredQuotes.length === 0 ? (
              <Paper sx={{ p: 4, textAlign: "center", borderRadius: 3 }}>
                <DescriptionIcon sx={{ fontSize: 48, color: "#d1d5db", mb: 1 }} />
                <Typography sx={{ color: "#64748b" }}>
                  {quotes?.length === 0 ? t("No quotes yet. Create your first one!") : t("No quotes match your search")}
                </Typography>
              </Paper>
            ) : (
              <Stack spacing={1.5}>
                {filteredQuotes.map((quote) => {
                  const status = STATUS_COLORS[quote.status] || STATUS_COLORS.draft
                  const updatedAt = quote.updated_at ? new Date(quote.updated_at).toLocaleDateString("el-GR", { day: "2-digit", month: "2-digit", year: "numeric" }) : ""
                  const itemCount = quote.item_count || 0
                  const total = parseFloat(quote.total_price) || 0

                  return (
                    <Paper
                      key={quote.id}
                      elevation={0}
                      onClick={() => handleLoadQuote(quote)}
                      sx={{
                        p: 2, borderRadius: 2, cursor: "pointer",
                        border: "1px solid #e2e8f0", transition: "all 0.15s",
                        "&:hover": { borderColor: ELITON.red, boxShadow: "0 4px 12px rgba(0,0,0,0.06)", transform: "translateX(4px)" },
                      }}
                    >
                      <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.5 }}>
                            <Typography sx={{ fontWeight: 700, fontSize: "0.92rem", color: "#1e293b" }}>
                              {quote.title || t("Untitled Quote")}
                            </Typography>
                            <Chip
                              label={t(status.label)}
                              size="small"
                              sx={{ height: 20, fontSize: "0.58rem", fontWeight: 700, bgcolor: status.bg, color: status.color }}
                            />
                          </Stack>
                          <Stack direction="row" spacing={2} alignItems="center">
                            {quote.customer_name && (
                              <Stack direction="row" alignItems="center" spacing={0.5}>
                                <PersonIcon sx={{ fontSize: 14, color: "#94a3b8" }} />
                                <Typography sx={{ fontSize: "0.75rem", color: "#64748b" }}>
                                  {quote.customer_name}
                                </Typography>
                              </Stack>
                            )}
                            {updatedAt && (
                              <Stack direction="row" alignItems="center" spacing={0.5}>
                                <CalendarTodayIcon sx={{ fontSize: 12, color: "#94a3b8" }} />
                                <Typography sx={{ fontSize: "0.7rem", color: "#94a3b8" }}>
                                  {updatedAt}
                                </Typography>
                              </Stack>
                            )}
                            {itemCount > 0 && (
                              <Stack direction="row" alignItems="center" spacing={0.5}>
                                <ShoppingCartIcon sx={{ fontSize: 12, color: "#94a3b8" }} />
                                <Typography sx={{ fontSize: "0.7rem", color: "#94a3b8" }}>
                                  {itemCount} {t("items")}
                                </Typography>
                              </Stack>
                            )}
                          </Stack>
                        </Box>
                        <Stack alignItems="flex-end" spacing={0.5}>
                          {total > 0 && (
                            <Typography sx={{ fontWeight: 700, fontSize: "0.95rem", color: "#1e293b" }}>
                              {"\u20AC"}{total.toFixed(2)}
                            </Typography>
                          )}
                          <ArrowForwardIcon sx={{ fontSize: 18, color: "#94a3b8" }} />
                        </Stack>
                      </Stack>
                    </Paper>
                  )
                })}
              </Stack>
            )}
          </Box>
        </Fade>
      </Box>
    </Box>
  )
}

export default QuoteGatePage
